/**
 * useNodeActions — Common action handlers for agent node toolbars
 */

import { useCallback } from 'react'
import { toast } from 'sonner'
import { useCanvasOptional } from '../../CanvasContext'
import { authenticatedFetch, buildProjectAssetUrl, getAuthToken, getConfiguredApiBase, getPublicationToken, createDebug } from '@ponpon/api-client'
import { useProjectId } from '../../../../contexts/ProjectIdContext'
const downloadLog = createDebug('dw:asset-download')

/**
 * Build a download URL: inject the JWT (or publication token) so the asset
 * endpoint authenticates without needing an Authorization header, and add
 * ?download=<filename> so the server responds with Content-Disposition:
 * attachment.
 */
export async function buildDownloadUrl(url: string, filename: string): Promise<string> {
  const u = new URL(url, window.location.origin)
  if (!u.searchParams.get('token')) {
    const token = await getAuthToken()
    if (token) u.searchParams.set('token', token)
  }
  if (!u.searchParams.get('publication_token')) {
    const pubToken = getPublicationToken()
    if (pubToken) u.searchParams.set('publication_token', pubToken)
  }
  u.searchParams.set('download', filename)
  return u.toString()
}

/** Typed custom events for cross-component communication */
interface AppEventMap {
  'add-node-to-chat': { nodeId: string }
}

export function dispatchAppEvent<K extends keyof AppEventMap>(
  type: K,
  detail: AppEventMap[K],
) {
  window.dispatchEvent(new CustomEvent(type, { detail }))
}

export function listenAppEvent<K extends keyof AppEventMap>(
  type: K,
  handler: (detail: AppEventMap[K]) => void,
): () => void {
  const listener = (event: Event) => {
    if (event instanceof CustomEvent) {
      handler(event.detail)
    }
  }
  window.addEventListener(type, listener)
  return () => window.removeEventListener(type, listener)
}

interface UseNodeActionsOptions {
  nodeId: string
}

export function useNodeActions({ nodeId }: UseNodeActionsOptions) {
  const canvas = useCanvasOptional()
  const projectId = useProjectId()

  const handleDelete = useCallback(() => {
    canvas?.removeNode(nodeId)
  }, [canvas, nodeId])

  const handleDuplicate = useCallback(() => {
    canvas?.duplicateNode(nodeId)
  }, [canvas, nodeId])

  const handleDownload = useCallback(async (assetPath: string, filename: string) => {
    downloadLog('start assetPath=%s filename=%s', assetPath, filename)
    if (!projectId) {
      downloadLog('abort: no project context')
      toast.error('Download failed: no project context')
      return
    }
    const url = buildProjectAssetUrl(projectId, assetPath)
    try {
      const downloadUrl = await buildDownloadUrl(url, filename)
      downloadLog('downloadUrl=%s', downloadUrl)
      const iframe = document.createElement('iframe')
      iframe.style.display = 'none'
      iframe.src = downloadUrl
      document.body.appendChild(iframe)
      window.setTimeout(() => {
        if (iframe.parentNode) iframe.parentNode.removeChild(iframe)
      }, 60_000)
    } catch (err) {
      downloadLog('error: %o', err)
      console.error('Download failed', err)
      toast.error(`Download failed: ${err instanceof Error ? err.message : 'Unknown error'}`)
    }
  }, [projectId])

  const handleAddToChat = useCallback(() => {
    dispatchAppEvent('add-node-to-chat', { nodeId })
  }, [nodeId])

  const handleSaveToLibrary = useCallback(async () => {
    try {
      const resp = await authenticatedFetch(
        `${getConfiguredApiBase()}/library/nodes`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ source_project_id: projectId, node_id: nodeId }),
        }
      )
      if (!resp.ok) {
        const err = await resp.json().catch(() => null)
        throw new Error(err?.detail || resp.statusText)
      }
      toast.success('Saved to library')
    } catch (err) {
      toast.error(`Failed to save: ${err instanceof Error ? err.message : 'Unknown error'}`)
    }
  }, [nodeId, projectId])

  return { handleDelete, handleDuplicate, handleDownload, handleAddToChat, handleSaveToLibrary }
}
